import {useEffect, useMemo, useState} from "react";
import {
    Box,
    Heading,
    Progress,
    Text,
} from "@chakra-ui/react";

import {BASE_URL, quizSteps} from "../constants";
import ChoiceStep from "./ChoiceStep";
import TextInputStep from "./TextInputStep";
import VerificationStep from "./VerificationStep";
import StepNavigation from "./StepNavigation";

const Quiz = () => {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const currentAnswer = useMemo(() => answers[step] || "", [answers, step]);
    const currentStep = useMemo(() => quizSteps[step], [step]);
    const totalSteps = quizSteps.length - 1;
    const [maxStepReached, setMaxStepReached] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const [code, setCode] = useState(["", "", "", ""])
    const [isLoading, setIsLoading] = useState(false);

    const sendCode = async (isResend) => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            const email = isResend ? answers[step - 1] : currentAnswer;
            const url = `${process.env.PUBLIC_URL}/api/sendCode`;

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: email
            });
            if (!response.ok) {
              const errorMessage = await response.text();
              throw new Error(errorMessage || "Failed to send verification code.");
            }
        } catch (e) {
            setErrorMessage(e.message)
        } finally {
            setIsLoading(false);
        }
    }

    const verifyCode = async () => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            const email = answers[step - 1];
            const codeJoined = code.join('')
            const url = `${process.env.PUBLIC_URL}/api/verifyCode`;

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({email, code: codeJoined})
            });
            if (!response.ok) {
              const errorMessage = await response.text();
              throw new Error(errorMessage || "Failed to validate verification code.");
            }

            setErrorMessage("")
            if (step === maxStepReached && typeof currentStep.triggerConversion === "function") {
              currentStep.triggerConversion();
            }

            setStep((s) => {
              const nextStep = s + 1;
              setMaxStepReached((prev) => Math.max(prev, nextStep));
              return nextStep;
            });
        } catch (e) {
            setErrorMessage(e.message);
        } finally {
            setIsLoading(false);
        }
    }

    const handleAnswer = (val) => {
        setAnswers((prev) => ({...prev, [step]: val}));
        setErrorMessage("");

        if (currentStep.type === "choice") {
          if (step === maxStepReached && typeof currentStep.triggerConversion === "function") {
            currentStep.triggerConversion();
          }

          setStep((s) => {
            const nextStep = s + 1; // since changes in answers are not immediate
            setMaxStepReached((prev) => Math.max(prev, nextStep));
            return nextStep;
          });
        }
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        // Call triggerConversion only if going to a new step
        if (step === maxStepReached && typeof currentStep.triggerConversion === "function") {
          currentStep.triggerConversion();
        }

        await sendCode();

        setStep((s) => {
          const nextStep = s + 1;
          setMaxStepReached((prev) => Math.max(prev, nextStep));
          return nextStep;
        });
    };

    const validate = () => {
        const val = currentAnswer.toString().trim();
        if (currentStep.type === "choice" && !val) {
            setErrorMessage("Please select an option");
            return false;
        }
        if (currentStep.type === "text" && !val) {
            setErrorMessage("This field cannot be empty");
            return false;
        }
        if (currentStep.type === "email" && !emailRx.test(val)) {
            setErrorMessage("Enter a valid email address");
            return false;
        }
        setErrorMessage("");
        return true;
    };

    const handleNextStep = () => {
        if (!validate()) return;

        // Call triggerConversion only if going to a new step
        if (step === maxStepReached && typeof currentStep.triggerConversion === "function") {
          currentStep.triggerConversion();
        }

        setStep((s) => {
          const nextStep = s + 1;
          setMaxStepReached((prev) => Math.max(prev, nextStep));
          return nextStep;
        });
    };


    const handlePrevStep = () => {
        setErrorMessage("");
        if (step > 0) return setStep((s) => s - 1);
    };

    useEffect( () => {
      const timer = setTimeout(() => {
        if (code.every(char => char !== "")) {
          verifyCode();
        }
      }, 200);

      return () => clearTimeout(timer);
    }, [code]);

    return (
        <Box height={"100%"} display={"flex"} flexDirection="column" px={{ base: 4, md: 10 }} py={2}>
            <Progress.Root
                value={(step / (totalSteps)) * 100}
                size={{base: "lg", md: "xl"}} mb={8}
                shape={"full"}
            >
                <Progress.Track>
                    <Progress.Range bg={"green.500"}/>
                </Progress.Track>
                <Progress.Label/>
                <Progress.ValueText fontSize={{base: "xs", md: "sm"}}/>
            </Progress.Root>

            <Heading size={{base: "md", md: "2xl"}} mb={6} textAlign="center">
                {currentStep.question}
            </Heading>

            {currentStep.type === "choice" && (
                <ChoiceStep
                    options={currentStep.options}
                    onSelect={handleAnswer}
                    selectedValue={currentAnswer}
                    isError={errorMessage !== ""}
                />
            )}

            {(currentStep.type === "text" || currentStep.type === "email") && (
                <TextInputStep
                    value={currentAnswer}
                    onChange={handleAnswer}
                    placeholder={currentStep.placeholder}
                    isError={errorMessage !== ""}
                />
            )}

            {currentStep.type === "email_verification" && (
                <VerificationStep
                    code={code}
                    onCodeChange={setCode}
                    onResend={() => sendCode(true)}
                />
            )}

            {currentStep.type === "message" && (
                <Box textAlign="center" mb={6}>
                  {currentStep.description && (
                      <Text fontSize="md !important" color="gray.600 !important">
                        {currentStep.description}
                      </Text>
                  )}
                </Box>
            )}

            {errorMessage && (
                <Box
                    px={{ base: 2, md: currentStep.type === "choice" ? 16 : 0 }}
                    w="100%"
                    mt={2}
                >
                    <Text
                        color="red.500 !important"
                        fontSize={{ base: "sm !important", md: "lg !important" }}
                        textAlign="center"
                        mb={{base: 4, md: 4}}
                    >
                        {errorMessage}
                    </Text>
                </Box>
            )}

            <StepNavigation
                step={step}
                totalSteps={totalSteps}
                isLoading={isLoading}
                onPrev={handlePrevStep}
                onNext={handleNextStep}
                onSubmit={handleSubmit}
                onVerify={verifyCode}
            />

            <Text fontSize={{base: "sm !important", md: "md !important"}} mt={4} textAlign="center" color="gray.500 !important">
                ⏱ It only takes a minute!
            </Text>
        </Box>
    );
};

export default Quiz;
